import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import type { IFilterData } from '@rankmi/rankmi-table/dist/interfaces/filterProps'

import { canSkipOnRowSelected } from 'helpers'
import { UnArray } from 'helpers/types.helper'
import TableSegments from 'components/ui/TableSegments'
import { Segment } from 'models/segment.model'
import {
  ListSegmentsParams,
  listSegments,
} from 'api/administration/segments/list'
import { SortValue } from 'models/rankmiTable.model'
import { useSegmentsColumns } from '../../../common/hooks'
import { LoadingDataProgress } from '../../../common/LoadingDataProgress'
import styles from './SelectSegments.module.scss'

interface SelectSegmentsProps {
  selectedMap: Map<number, Segment>
  segmentsAdded: Segment[]
  onAdd: (users: Segment[]) => void
  onRemove?: (users: Segment[]) => void
  filterData?: IFilterData
  onStartLoading?: () => void
  onStopLoading?: () => void
  filter?(users: Segment[]): Segment[]
  onOpenSegment?(segment: Segment): void
}

export const SelectSegments: React.FC<SelectSegmentsProps> = ({
  selectedMap,
  segmentsAdded,
  onAdd,
  onRemove,
  filterData,
  onStartLoading,
  onStopLoading,
  onOpenSegment,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortValue>({})
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  const [loading, setLoading] = useState(false)
  const [loadingAll, setLoadingAll] = useState(false)
  const [list, setList] = useState<Segment[]>([])
  const [total, setTotal] = useState(0)

  const params = useMemo(
    (): Omit<ListSegmentsParams, 'page' | 'perPage'> => ({
      searchText: search,
      sort,
      filterData: filterData,
    }),
    [filterData, search, sort]
  )

  const selected = useMemo(
    () => Array.from(selectedMap.values()),
    [selectedMap]
  )

  const onAddRef = useRef(onAdd)
  useEffect(() => {
    onAddRef.current = onAdd
  }, [onAdd])

  useEffect(() => {
    setLoading(true)
    listSegments({
      page,
      perPage,
      ...params,
    })
      .then(({ data, total }) => {
        setList(data)
        setTotal(total)
      })
      .finally(() => setLoading(false))
  }, [page, params, perPage])

  const dataSource = useMemo(
    () =>
      list.map((user) => ({
        key: user.id,
        ...user,
      })),
    [list]
  )

  const baseColumns = useSegmentsColumns()
  const columns = useMemo(
    () => [
      ...baseColumns,
      {
        title: '',
        key: 'action',
        width: 100,
        render: (_, participant: UnArray<typeof dataSource>) => (
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              className="negative-primary"
              size="sm"
              onClick={() => {
                onOpenSegment?.(participant)
              }}
            >
              {t('configuration.segments.common.viewSegment')}
            </Button>
          </div>
        ),
      },
    ],
    [baseColumns, onOpenSegment, t]
  )

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const [usersLoaded, setLoaded] = useState<Segment[]>([])
  const setLoadedRef = useRef(setLoaded)
  useEffect(() => {
    setLoadedRef.current = setLoaded
  }, [setLoaded])

  const handleAllSelected = useCallback(async () => {
    const limit = 150
    let fetchData = true
    let page = 1
    let segments: Segment[] = []

    setLoadingAll(true)
    onStartLoading?.()
    try {
      while (fetchData) {
        if (!onAdd) return
        const { data } = await listSegments({
          page,
          perPage: limit,
          ...params,
        })
        if (data.length === 0 || data.length < limit) fetchData = false
        segments = [...segments, ...data]
        setLoaded(segments)
        page++
      }
    } catch (e) {
      console.error(e)
    }
    setLoadingAll(false)
    onAdd(segments)
    onStopLoading?.()
    setLoaded([])
  }, [onAdd, onStartLoading, onStopLoading, params])

  const handleAllSelectedRef = useRef(handleAllSelected)

  useEffect(() => {
    handleAllSelectedRef.current = handleAllSelected
  }, [handleAllSelected])

  return (
    <div className={classNames('position-relative', styles.container)}>
      <TableSegments
        changeColumnsVisibility
        responsive
        delaySearch
        data={dataSource.filter(
          (segment) =>
            !segmentsAdded.map((item) => item.id).includes(segment.id)
        )}
        search={search}
        onSearch={handleSearch}
        total={total - segmentsAdded.length}
        columnHeaders={columns}
        onSortChange={(sortData: any) => setSort(sortData)}
        onRowSelected={({ selectedRows, selectedRowKeys, dispatcher }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          if (dispatcher === 'handleUnselectAllRows') {
            onRemove?.(selected)
            return
          }
          const newSelected = selectedRows.filter(
            ({ id }) => !selectedMap.has(id)
          ) as Segment[]

          const keysSet = new Set(selectedRowKeys)

          const deleted = selected.filter(({ id }) => !keysSet.has(id))

          // Rankmi-table has a bug, use onAllRowKeys how reference
          if (newSelected.length) onAddRef.current(selectedRows)

          if (deleted.length) onRemove?.(deleted)
        }}
        selectedRows={selected.map(({ id }) => id)}
        loading={loading || loadingAll}
        onPaginationChange={(currentPage: number) => setPage(currentPage)}
        simpleSelectAll={true}
        onRowsPerPageChange={(itemsPerPage: number) => setPerPage(itemsPerPage)}
        onAllRowKeys={() => {
          // Rankmi-table has a bug, use onAllRowKeys how reference
          handleAllSelectedRef.current()
        }}
        showScrollButtons
      />
      {loadingAll && (
        <LoadingDataProgress loaded={usersLoaded.length} total={total} />
      )}
    </div>
  )
}

export default SelectSegments
